import { AnnotatedType, PrimaryAnnotatedType } from '@/components/annotated-type';
import { HorizontalBarChart } from '@/components/bar-chart/horizontal';
import type { BarChartItem } from '@/components/bar-chart/vertical';
import { StoryQuestion, useStoryQuestion } from '@/components/microstory/question';
import {
  OnStepEnterExitArgs,
  ScrollGraphic,
  ScrollRoot,
  ScrollStep,
  ScrollSteps,
} from '@/components/microstory/scroll';
import { StoryText } from '@/components/microstory/text';
import { StripPlot } from '@/components/strip-plot';
import { countryIdToName } from '@/utils/country-id-to-name';
import { useNumberFormatting } from '@/utils/use-number-formatting';
import { t, Trans } from '@lingui/macro';
import { useState } from 'react';

type GlobalOutlookChapter1Props = {
  data: GlobalOutlookData;
};

export function GlobalOutlookChapter1(props: GlobalOutlookChapter1Props) {
  const answers = [
    { id: 'better', label: t({ id: 'global-outlook.question-1.answer-1', message: 'Better' }) },
    { id: 'worse', label: t({ id: 'global-outlook.question-1.answer-2', message: 'Worse' }) },
  ];
  const formatPercentage = useNumberFormatting('percent');

  const { selectedAnswer, handleAnswer } = useStoryQuestion<(typeof answers)[number]>();

  const [barChartData, setBarChartData] = useState<BarChartItem[]>([
    {
      label: t({ id: 'global-outlook.chart-1.better', message: 'Better place' }),
      value: props.data.better_place.younger.better,
    },
    {
      label: t({ id: 'global-outlook.chart-1.worse', message: 'Worse place' }),
      value: props.data.better_place.younger.worse,
    },
  ]);
  const [barChartVisible, setBarChartVisible] = useState(false);

  const handleFirstStepEnter = async (evt: OnStepEnterExitArgs<BarChartItem[]>) => {
    setBarChartVisible(true);
    if (evt.data) setBarChartData(evt.data);
  };

  const [highlightedIds, setHighlightedIds] = useState<string[]>();

  const handleSecondStepEnter = async (evt: OnStepEnterExitArgs<string[]>) => {
    if (evt.data) setHighlightedIds(evt.data);
  };

  const stripPlotData = (
    Object.entries(props.data.better_place_bd) as [CountryIsoCode, AgeGroupBreakdown][]
  ).map(([key, value]) => ({
    id: key,
    label: countryIdToName(key),
    ...value,
  }));

  return (
    <>
      <StoryQuestion
        question={t({
          id: 'global-outlook.question-1.question',
          message:
            'Do you think the world is becoming a better or worse place with each generation?',
        })}
        answers={answers}
        onAnswer={handleAnswer}
        selectedAnswer={selectedAnswer}
        response={
          selectedAnswer?.id === 'better'
            ? t({
                id: 'global-outlook.question-1.response-1',
                message: 'Most young people feel the same',
              })
            : t({
                id: 'global-outlook.question-1.response-2',
                message: 'Young people tend to disagree',
              })
        }
      />

      <ScrollRoot>
        <ScrollGraphic>
          <HorizontalBarChart
            label={t({
              id: 'global-outlook.chapter-1.chart-label',
              message: '% who think the world is becoming a',
            })}
            formatValue={formatPercentage}
            data={barChartData}
            visible={barChartVisible}
          />
        </ScrollGraphic>

        <ScrollSteps onStepEnter={handleFirstStepEnter} offset={0.5} hasGraphic>
          <ScrollStep
            data={[
              {
                label: t({ id: 'global-outlook.chart-1.better', message: 'Better place' }),
                value: props.data.better_place.younger.better,
              },
              {
                label: t({ id: 'global-outlook.chart-1.worse', message: 'Worse place' }),
                value: props.data.better_place.younger.worse,
              },
            ]}
          >
            <StoryText>
              <Trans id="global-outlook.chapter-1.text-1">
                On average, three out of five young people say they think the world is{' '}
                <AnnotatedType>progressing toward a better future.</AnnotatedType>
              </Trans>
            </StoryText>
          </ScrollStep>
        </ScrollSteps>
      </ScrollRoot>

      <ScrollRoot>
        <ScrollGraphic>
          <StripPlot
            data={stripPlotData}
            formatValue={formatPercentage}
            getValue={(d) => d.younger}
            highlightedIds={highlightedIds}
            axisLabel={t({
              id: 'global-outlook.chart-2.label',
              message: '% of young people who think the world is becoming a better place',
            })}
          />
        </ScrollGraphic>

        <ScrollSteps onStepEnter={handleSecondStepEnter} offset={0.5} hasGraphic>
          <ScrollStep>
            <StoryText>
              <Trans id="global-outlook.chapter-1.text-2">
                While optimism is common among the young, its degree varies from place to place.
              </Trans>
            </StoryText>
          </ScrollStep>

          <ScrollStep data={['ID']}>
            <StoryText>
              <Trans id="global-outlook.chapter-1.text-3">
                It is highest in {countryIdToName('ID')} where{' '}
                {formatPercentage(props.data.better_place_bd.ID.younger)} of{' '}
                <PrimaryAnnotatedType>young people</PrimaryAnnotatedType> believe the world is
                becoming a better place.
              </Trans>
            </StoryText>
          </ScrollStep>

          <ScrollStep data={['ML']}>
            <StoryText>
              <Trans id="global-outlook.chapter-1.text-4">
                By contrast, only {formatPercentage(props.data.better_place_bd.ML.younger)} of{' '}
                <PrimaryAnnotatedType>young people</PrimaryAnnotatedType> in {countryIdToName('ML')}{' '}
                share this positive outlook for our world.
              </Trans>
            </StoryText>
          </ScrollStep>
        </ScrollSteps>
      </ScrollRoot>
    </>
  );
}
